<!--

Link two Contents on a Decoder (so actually their entries) so that
during playlist generation they are played one after another.

-->

<template>
  <v-dialog
    :value="show"
    :width="$store.state.common.largeDialogWidth"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    scrollable
    @input="(val) => $emit('update:show', val)"
  >
    <v-card>
      <v-card-title>
        <v-layout column>
          <v-layout>
            <v-icon
              medium
              class="msr--text mr-2"
            >
              link
            </v-icon>
            <div class="headline">
              {{ $t('linkContents') }}
            </div>
            <v-btn
              class="ml-auto mr-0 my-0"
              flat
              icon
              :title="$t('close')"
              :loading="loading"
              @click="abort()"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
          <div class="subheading mt-2">
            {{ $t('linkContentsHint_1') }}
            <span class="msr--text">{{ entry.spot.title }}</span>
            {{ $t('linkContentsHint_2') }}
          </div>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <v-list
          v-if="validChoices.length > 0"
          dense
          two-line
        >
          <link-spot-entry-tile
            v-for="entryChoice in validChoices"
            :key="entryChoice.id"
            :entry="entryChoice"
            :disabled="loading"
            :currently-linked="
              !!entry.play_after_entry &&
                entry.play_after_entry.id === entryChoice.id
            "
            @selected="linkTo"
            @deselected="unlinkFrom"
          />
        </v-list>
        <v-subheader
          v-if="validChoices.length === 0"
          class="py-0 auto-height"
        >
          {{ $t('linkContentsNoValidChoices') }}
        </v-subheader>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import LinkSpotEntryTile from '@/components/LinkSpotEntryTile';

export default {
  components: {
    LinkSpotEntryTile,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    /**
     * The TVSpotEntry to link against.
     */
    entry: {
      type: Object,
      required: true,
    },
    /**
     * TVSpotEntry candidates to link with one of them (aka run afterwards).
     */
    entryChoices: {
      type: Array[Object],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    /**
     * Disallow entries that are part of a link already (as parent or child).
     *
     * This basically enforce that we can only link two SpotEntries, no
     * more.
     *
     * TODO: This is an artificial limitation for now: We could link
     *   entries into any non-looped dependency chain without problems,
     *   but we'd need a more elaborated check here whether the current
     *   entry is already part of some other entry's dependency chain.
     *   Let's postpone this until this feature is actually requested.
     */
    validChoices() {
      const linkedEntryIds = this.entryChoices.map((otherEntry) => {
        return otherEntry.play_after_entry
          ? otherEntry.play_after_entry.id
          : null;
      });
      const isAlreadyPartOfRelation =
        linkedEntryIds.indexOf(this.entry.id) !== -1;
      if (isAlreadyPartOfRelation) {
        return [];
      }

      const filteredChoices = this.entryChoices.filter((otherEntry) => {
        const isParent =
          this.entry.play_after_entry &&
          this.entry.play_after_entry.id === otherEntry.id;
        const isNotYetLinked =
          otherEntry.play_after_entry === undefined &&
          linkedEntryIds.indexOf(otherEntry.id) === -1;
        return isParent || isNotYetLinked;
      });
      return filteredChoices;
    },
  },
  methods: {
    closeDialog() {
      this.$emit('update:show', false);
    },
    abort() {
      this.closeDialog();
    },
    linkTo(entryChoice, successMessage = 'linkContentsSuccess') {
      this.loading = true;

      const promise = this.$api.linkAgainstSpotEntry(
        this.entry.ident,
        entryChoice.id
      );

      promise
        .then((response) => {
          // We need to rerender the parent SpotEntry after this, but
          // it's not enough to set the attribute here: As the entry
          // is a prop that is attached to a list on the Decoder
          // instance we need to refetch that Decoder by piping this
          // event upwards.
          this.$emit('changed');

          this.$bus.$emit('alert-success', {
            message: successMessage,
            translate: true,
          });
          this.closeDialog();
        })
        .finally(() => {
          this.loading = false;
        });

      return promise;
    },
    /**
     * Send an empty string as ID to unlink.
     */
    unlinkFrom(entryChoice) {
      const successMessage = 'unlinkContentSuccess';
      return this.linkTo({id: ''}, successMessage);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/LinkContentsDialog.vue